class MapSection {
    constructor() {
        this.init();
    }

    init() {
        this.container = document.querySelector("#mapSectionContainer");
        if(!this.container) return;
        this.mapZoom = +this.container.getAttribute("data-map-zoom");
        this.apiKey = this.container.getAttribute("data-api-key");
        this.initialAddress = this.container.getAttribute("data-init-address");
        this.clusterBackgroundColor = this.container.getAttribute("data-cluster-background-color");
        this.clusterTextColor = this.container.getAttribute("data-cluster-text-color");
        this.clusterImage = this.container.getAttribute("data-cluster-image");
        this.mapStyle = this.container.getAttribute("data-mapbox-style");
        this.markersObject = JSON.parse(this.container.querySelector("[data-mapbox-markers]").innerHTML);
        this.map;
        this.modal = this.container.querySelector("[data-modal-map]");
        this.modalContent = this.modal.querySelector("[data-modal-content-map]");
        this.markers = [];
        this.markerClusterMarkers = [];

        this.initMap();
    }

    async initMap() {
        if(!mapboxgl) {
            return;
        }

        mapboxgl.accessToken = this.apiKey;
        const initCoordResponse = await this.getCoordFromGeocoder(this.initialAddress, 1);

        if (!initCoordResponse) return;

        this.map = new mapboxgl.Map({
            container: 'map',
            style: this.mapStyle === "light" ? 'mapbox://styles/mapbox/light-v11' : 'mapbox://styles/mapbox/dark-v11',
            center: [...initCoordResponse[0]],
            zoom: this.mapZoom,
        });

        for (let i = 0; i < this.markersObject.length; i++) {
            const marker = this.markersObject[i];
            const markerEl = this.returnMarkerHtml(marker);
            const mapMarker = new mapboxgl.Marker(markerEl);
            const markerCoordinate = await this.getCoordFromGeocoder(marker.address, 1);

            mapMarker.setLngLat([...markerCoordinate[0]]);
            this.markers.push({
                id: marker.id,
                title: marker.title,
                coordinates: [...markerCoordinate[0]],
                html: `${markerEl.outerHTML}`,
                mapMarker: mapMarker
            })
        }

        this.initMapNavigation();
        this.initCluster();
    }

    initCluster() {
        const data = {
            "type": "FeatureCollection",
            "features": [...this.markers.map(marker => {
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Marker',
                        coordinates: [...marker.coordinates]
                    },
                    properties: {
                        id: marker.id,
                        html: `${marker.html}`
                    }
                }
            })]
        }

        this.map.addSource('markers', {
            type: 'geojson',
            data: data,
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: 30
        });

        if (this.clusterImage) {
            this.map.loadImage(
                `${this.clusterImage}`, 
                (error, image) => {
                    if (error) throw error;
                    this.map.addImage('clusterImage', image);
            
                    this.map.addLayer({
                        id: 'clusters',
                        type: 'symbol',
                        source: 'markers',
                        filter: ['has', 'point_count'],
                        layout: {
                            "icon-image": "clusterImage",
                            'icon-size': 1,
                            'icon-allow-overlap': true
                        },
                    });

                    this.map.addLayer({
                        id: 'cluster-count',
                        type: 'symbol',
                        source: 'markers',
                        filter: ['has', 'point_count'],
                        layout: {
                            'text-field': ['get', 'point_count_abbreviated'],
                            'text-size': 16,
                            'text-allow-overlap': true
                        },
                        paint: {
                            "text-color": this.clusterTextColor
                        }
                    });
                }
            );
        } else {
            this.map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'markers',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [ 'step', ['get', 'point_count'], this.clusterBackgroundColor, 100, this.clusterBackgroundColor, 750, this.clusterBackgroundColor],
                    'circle-radius': [ 'step', ['get', 'point_count'], 20, 100, 30, 750, 40],
                },
            });

            this.map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'markers',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-size': 16,
                    'text-allow-overlap': true
                },
                paint: {
                    "text-color": this.clusterTextColor
                }
            });
        }

        const markers = {};
        let markersOnScreen = {};
 
        const updateMarkers = () => {
            const newMarkers = {};
            const features = this.map.querySourceFeatures('markers');
            const parser = new DOMParser();

            for (const feature of features) {
                const coords = feature.geometry.coordinates;
                const props = feature.properties;
                if (props.cluster) continue;
                const id = props.id;
                const markerEl = parser.parseFromString(props.html, "text/html").querySelector("[data-title]");

                markerEl.addEventListener("click", () => {
                    const markerTitle = markerEl.dataset.title.toLowerCase().replaceAll(" ", "-");
                    const button = document.querySelector(`[data-store-button-id="${markerTitle}"]`);
                    const template = document.querySelector(`[data-template-id="${markerEl.dataset.id}"]`);
        
                    this.modalContent.innerHTML = "";
                    this.modalContent.append(template.content.firstElementChild.cloneNode(true));
        
                    if (this.modal.classList.contains("hidden")) {
                        this.modal.classList.remove("hidden")
                    }
        
                    this.container.querySelectorAll("[data-store-button-id]").forEach(button => button.classList.remove("active"));
        
                    if (button) {
                        if (!button.classList.contains("active")) button.classList.add("active");
                    }
                })

                let marker = markers[id];

                if (!marker) {
                    marker = markers[id] = new mapboxgl.Marker({
                        element: markerEl
                    }).setLngLat(coords);
                }
                newMarkers[id] = marker;
                if (!markersOnScreen[id]) marker.addTo(this.map);
            }

            for (const id in markersOnScreen) {
                if (!newMarkers[id]) {
                markersOnScreen[id].remove();
                } 
            }

            markersOnScreen = newMarkers;
        }
 
        this.map.on('render', () => {
            if (!this.map.isSourceLoaded('markers')) return;
            updateMarkers();
        });
    }

    initMapNavigation() {
        this.container.addEventListener("click", (e) => {
            if (e.target.closest("[data-store-button-id]")) {
                const target = e.target.closest("[data-store-button-id]");
                const markerId = target.getAttribute("data-store-button-id");
                this.container.querySelectorAll("[data-store-button-id]").forEach(button => button.classList.remove("active"));

                if (!target.classList.contains("active")) {
                    target.classList.add("active")
                }
                this.setActiveMarker(markerId);
            }

            if (e.target.closest("[data-modal-map-close]")) {
                this.closeModal();
            }
        })
    }

    closeModal() {
        this.modalContent.innerHTML = "";

        if (!this.modal.classList.contains("hidden")) {
            this.modal.classList.add("hidden")
        }

        this.container.querySelectorAll("[data-store-button-id]").forEach(button => button.classList.remove("active"));
    }

    setActiveMarker(markerId) {
        this.markers.forEach((marker) => {
            const markerTitle = marker.title.toLowerCase().replaceAll(" ", "-");

            if (markerTitle === markerId) {
                this.map.panTo(marker.mapMarker.getLngLat(), {
                    animate: true, 
                    duration: 1000,
                    curve: 1.42,
                    speed: 1.2,
                    zoom: this.mapZoom + 3,
                    essential: true
                });
            }
        });
    }

    async getCoordFromGeocoder(search_text, limit) {
        const coordinatesArr = [];
        const responseGeocode = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?access_token=${this.apiKey}&q=${search_text}&limit=${limit}`)
        .then((response) => {
            if (response.ok) {
                return response.json()
            }
        }).catch((e) => {
            console.error(e);
        });

        if (!responseGeocode && responseGeocode.features.length) return;
        
        responseGeocode.features.forEach(feature => {
            coordinatesArr.push(feature.geometry.coordinates);
        })
        
        return coordinatesArr
    }

    returnMarkerHtml(marker) {
        const el = document.createElement("div");
        const pictureEl = document.createElement("picture");
        const imgEl = document.createElement("img");
        imgEl.src = marker.icon;
        pictureEl.appendChild(imgEl);
        el.appendChild(pictureEl);
        el.style.width = "max-content";
        el.style.height = "max-content";
        el.dataset.title = marker.title;
        el.dataset.id = marker.id;

        return el
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const mapSection = new MapSection();
})

document.addEventListener("shopify:section:load", () => {
    const mapSection = new MapSection();
})